import { api, apiList, uploadFormData } from '../../api/client';
import { optimizeImageForUpload } from '../../utils/imageCompressor';

export interface FileAssetDto {
  id: number;
  original_filename: string;
  caption?: string;
  content_type?: string;
  media_kind?: 'image' | 'video' | 'document';
  size_bytes?: number;
  purpose?: string;
  visibility?: string;
  moderation_status?: string;
  url: string;
  download_url?: string;
}

export interface RequestPhotoDto {
  id: number;
  file: FileAssetDto;
}

export interface RequestPetDto {
  id: number;
  pet_type: 'dog' | 'cat' | 'rodent' | 'bird' | 'farm' | 'other';
  custom_type: string;
  breed: string;
  count: number;
  weight_kg: string | null;
  age: string;
  name: string;
}

export interface TransportRequestDto {
  id: number;
  is_saved?: boolean;
  customer_id: number;
  customer_name: string;
  customer_rating: string;
  customer_completed_requests_count: number;
  public_code: string | null;
  status: string;
  from_city: string;
  to_city: string;
  pickup_not_before: string | null;
  delivery_not_after: string | null;
  urgency: string;
  carrier_box: string;
  documents_status: string;
  pet_notes: string;
  feeding_notes: string;
  pickup_contact_name: string | null;
  pickup_contact_phone: string | null;
  pickup_contact_notes: string | null;
  delivery_contact_name: string | null;
  delivery_contact_phone: string | null;
  delivery_contact_notes: string | null;
  budget_min_minor: number | null;
  budget_max_minor: number | null;
  currency: string;
  published_at: string | null;
  pets: RequestPetDto[];
  photos: RequestPhotoDto[];
  questions: Array<{
    id: number;
    carrier: number;
    carrier_name: string;
    question: string;
    answer: string;
    moderation_status: 'pending' | 'approved' | 'rejected';
    answered_at: string | null;
    created_at: string;
  }>;
  offer_count: number;
}

export interface CarrierProfileDto {
  id: number;
  created_at: string;
  email?: string;
  phone?: string;
  phone_verified_at?: string | null;
  display_name: string;
  avatar: FileAssetDto | null;
  carrier_type: 'person' | 'company';
  service_type: 'auto' | 'air_courier' | 'cargo_broker' | 'airport_meet';
  first_name?: string;
  last_name?: string;
  company_name?: string;
  contact_name?: string;
  city: string;
  about: string;
  awards: string[];
  verification_status: string;
  review_rating: string;
  photo_rating_percent: number;
  verification_rating_percent: number;
  completed_transports_count: number;
  can_offer: boolean;
  preferred_routes: Array<{
    from_city: string;
    to_city: string;
  }>;
  vehicles: Array<{
    id: number;
    make_model: string;
    year: number | null;
    plate_number?: string;
    body_type: string;
    pet_features: string;
    is_primary: boolean;
  }>;
  portfolio_photos: FileAssetDto[];
  portfolio_photos_count: number;
  vehicle_photos: FileAssetDto[];
  recent_trips: Array<{
    from_city: string;
    to_city: string;
    completed_at: string;
    pet_type: 'dog' | 'cat' | 'rodent' | 'bird' | 'farm' | 'other';
    pet_name: string;
  }>;
  reviews: Array<{
    id: number;
    author_name: string;
    rating: number;
    comment: string;
    route: { from_city: string; to_city: string };
    photos: FileAssetDto[];
    created_at: string;
    moderation_status?: 'pending' | 'approved' | 'rejected';
  }>;
  reviews_count: number;
  approved_reviews_count?: number;
  push_enabled?: boolean;
  sms_active_transport?: boolean;
  email_marketing?: boolean;
  notification_preferences?: Record<string, Record<'push' | 'email' | 'sms', boolean>>;
  language?: string;
  region?: string;
  timezone?: string;
  display_currency?: 'RUB' | 'EUR';
}

export interface OfferDto {
  id: number;
  request: number;
  carrier: number;
  status: string;
  net_price_minor: number;
  platform_fee_minor: number;
  total_price_minor: number;
  currency: string;
  departure_date: string | null;
  delivery_date: string | null;
  expires_at: string | null;
  comment: string;
  carrier_name: string;
  carrier_is_deleted: boolean;
  carrier_service_type?: 'auto' | 'air_courier' | 'cargo_broker' | 'airport_meet';
  carrier_rating: string;
  carrier_photo_rating_percent: number;
  carrier_verification_rating_percent: number;
  carrier_completed_transports_count: number;
  portfolio_photos: FileAssetDto[];
  created_at: string;
}

export interface TransportDto {
  offer_status?: string;
  id: number;
  deal: number;
  status: string;
  delivered_at: string | null;
  customer_received_at: string | null;
  created_at: string;
  request_id: number;
  request_code: string;
  request_status: string;
  from_city: string;
  to_city: string;
  carrier_id: number;
  carrier_name: string;
  customer_id: number;
  customer_name: string;
  net_price_minor: number;
  platform_fee_minor: number;
  total_price_minor: number;
  currency: string;
  departure_date: string | null;
  delivery_date: string | null;
  payment_status: string;
  checkpoints: Array<{
    id: number;
    title: string;
    comment: string;
    occurred_at: string | null;
    created_at: string;
    media: number[];
    media_files: FileAssetDto[];
  }>;
}

export interface InvoiceDto {
  id: number;
  public_code: string;
  carrier: number;
  deal: number;
  request_code: string;
  from_city: string;
  to_city: string;
  amount_minor: number;
  currency: string;
  fee_percent: number;
  status: string;
  is_blocking: boolean;
  due_date: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface BillingTransactionDto {
  id: number;
  carrier: number;
  invoice: number | null;
  transaction_type: string;
  amount_minor: number;
  currency: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface BillingSummaryDto {
  balance: {
    available_minor: number;
    blocked_minor: number;
    currency: string;
  };
  open_invoices: InvoiceDto[];
  transactions: BillingTransactionDto[];
  can_offer: boolean;
  payment_requisites: {
    recipient: string;
    tax_id: string;
    kpp: string;
    account: string;
    bank: string;
    payment_purpose: string;
  };
  support_email: string;
  support_phone: string;
}

export interface BalanceTopUpDto {
  balance: BillingSummaryDto['balance'];
  transaction: BillingTransactionDto;
}

export interface ReviewDto {
  moderation_status: 'pending' | 'approved' | 'rejected';
  id: number;
  transport: number;
  author: number;
  target_user: number;
  author_role: 'customer' | 'carrier';
  rating_overall: number;
  criteria_ratings: Record<string, number>;
  comment: string;
  attached_photos: number[];
  created_at: string;
}

export interface DisputeDto {
  id: number;
  deal: number;
  opened_by: number;
  opened_by_role: 'customer' | 'carrier';
  reason: string;
  description: string;
  expected_resolution: string;
  status: string;
  evidence: number[];
  decision_text: string;
  resolved_at: string | null;
  created_at: string;
}

export interface NotificationDto {
  id: number;
  role: string;
  notification_type: string;
  title: string;
  text: string;
  target_screen: string;
  target_id: string;
  read_at: string | null;
  created_at: string;
}

export interface ChatDto {
  id: number;
  deal: number | null;
  offer: number | null;
  request: number;
  customer: number;
  carrier: number;
  request_code: string;
  from_city: string;
  to_city: string;
  customer_name: string;
  carrier_name: string;
  last_message: MessageDto | null;
  unread_count: number;
  phase: 'negotiation' | 'deal';
  status: 'active' | 'closed';
  can_message: boolean;
  created_at: string;
}

export interface MessageDto {
  id: number;
  chat: number;
  sender: number | null;
  message_type: 'text' | 'photo' | 'media' | 'system' | 'payment' | 'status';
  body: string;
  media: number | null;
  media_file: FileAssetDto | null;
  is_read_by_recipient: boolean;
  created_at: string;
}

export interface MessagePageDto {
  results: MessageDto[];
  next_before: number | null;
}

export interface ChatReadDto {
  chat_id: number;
  last_read_message_id: number;
  read_at: string;
  unread_count: number;
  total_unread_count: number;
}

export const carrierCabinetApi = {
  billing: () => api<BillingSummaryDto>('/api/billing/summary/'),
  chats: () => apiList<ChatDto>('/api/chats/'),
  confirmPayment: (dealId: number) => api(`/api/deals/${dealId}/payment/carrier-confirm/`, { method: 'POST' }),
  createDispute: (payload: {
    deal: number;
    opened_by_role: 'carrier';
    reason: string;
    description: string;
    expected_resolution: string;
    evidence?: number[];
  }) => api<DisputeDto>('/api/disputes/', { method: 'POST', body: payload }),
  createOffer: (payload: Record<string, unknown>) => api<OfferDto>('/api/offers/', { method: 'POST', body: payload }),
  createReview: (payload: {
    transport: number;
    author_role: 'carrier';
    rating_overall: number;
    criteria_ratings?: Record<string, number>;
    comment: string;
    attached_photos?: number[];
  }) => api<ReviewDto>('/api/reviews/', { method: 'POST', body: payload }),
  disputes: () => apiList<DisputeDto>('/api/disputes/'),
  feed: () => apiList<TransportRequestDto>('/api/requests/feed/'),
  askQuestion: (requestId: number, question: string) => api(`/api/requests/${requestId}/questions/`, {
    method: 'POST',
    body: { question },
  }),
  markAllNotificationsRead: () => api<{ updated: number }>('/api/notifications/read-all/', { method: 'POST' }),
  markNotificationRead: (id: number) => api<NotificationDto>(`/api/notifications/${id}/read/`, { method: 'POST' }),
  messages: (chatId: number) => apiList<MessageDto>(`/api/chats/${chatId}/messages/`),
  messagesPage: (chatId: number, before?: number) => api<MessagePageDto>(
    `/api/chats/${chatId}/messages/?limit=50${before ? `&before=${before}` : ''}`,
  ),
  markChatRead: (chatId: number, lastMessageId: number) => api<ChatReadDto>(`/api/chats/${chatId}/read/`, {
    method: 'POST',
    body: { last_message_id: lastMessageId },
  }),
  unreadMessages: () => api<{ total_unread: number }>('/api/chats/unread-count/'),
  notifications: () => apiList<NotificationDto>('/api/notifications/'),
  offers: () => apiList<OfferDto>('/api/offers/'),
  invoices: () => apiList<InvoiceDto>('/api/billing/invoices/'),
  invoicePdfUrl: (invoiceId: number) => `/api/billing/invoices/${invoiceId}/pdf/`,
  payInvoiceFromBalance: (invoiceId: number) => api<InvoiceDto>(`/api/billing/invoices/${invoiceId}/pay-from-balance/`, { method: 'POST' }),
  testTopUpBalance: (amountMinor: number) => api<BalanceTopUpDto>('/api/billing/balance/test-top-up/', {
    method: 'POST',
    body: { amount_minor: amountMinor },
  }),
  profile: () => api<CarrierProfileDto>('/api/profiles/me/carrier/'),
  sendEmailVerification: () => api<{ sent: boolean; already_verified?: boolean; email?: string }>('/api/auth/email/send/', {
    method: 'POST',
  }),
  publicProfile: (id: number) => api<CarrierProfileDto>(`/api/profiles/carriers/${id}/public/`),
  changePassword: (currentPassword: string, newPassword: string) => api('/api/auth/password/change/', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  }),
  updateProfile: (payload: Record<string, unknown>) => api<CarrierProfileDto>('/api/profiles/me/carrier/', { method: 'PATCH', body: payload }),
  request: (id: number) => api<TransportRequestDto>(`/api/requests/${id}/`),
  setRequestSaved: (id: number, saved: boolean) => api<{ is_saved: boolean }>(`/api/requests/${id}/saved/`, {
    method: saved ? 'PUT' : 'DELETE',
  }),
  sendMessage: (chatId: number, body: string, media?: FileAssetDto) => api<MessageDto>(`/api/chats/${chatId}/messages/`, {
    method: 'POST',
    body: { body, message_type: media ? 'photo' : 'text', media: media?.id ?? null },
  }),
  reviews: () => apiList<ReviewDto>('/api/reviews/'),
  startTransport: (transportId: number) => api<TransportDto>(`/api/transports/${transportId}/start/`, { method: 'POST' }),
  deliverTransport: (transportId: number) => api<TransportDto>(`/api/transports/${transportId}/deliver/`, { method: 'POST' }),
  createCheckpoint: (
    transportId: number,
    payload: { title: string; comment?: string; occurred_at?: string; media: number[] },
  ) => api<TransportDto['checkpoints'][number]>(`/api/transports/${transportId}/checkpoints/`, {
    method: 'POST',
    body: payload,
  }),
  transports: () => apiList<TransportDto>('/api/deals/transports/'),
  transactions: () => apiList<BillingTransactionDto>('/api/billing/transactions/'),
  uploadChatAttachment: async (file: File, onProgress?: (percent: number) => void) => {
    const optimized = await optimizeImageForUpload(file);
    const body = new FormData();
    body.append('file', optimized);
    body.append('purpose', 'chat_attachment');
    body.append('visibility', 'deal');
    return uploadFormData<FileAssetDto>('/api/media/', body, onProgress);
  },
  uploadChatPhoto: async (file: File) => {
    const optimized = await optimizeImageForUpload(file);
    const body = new FormData();
    body.append('file', optimized);
    body.append('purpose', 'chat_attachment');
    body.append('visibility', 'deal');
    return api<FileAssetDto>('/api/media/', { method: 'POST', body });
  },
  uploadCheckpointMedia: async (file: File, onProgress?: (percent: number) => void) => {
    const optimized = await optimizeImageForUpload(file);
    const body = new FormData();
    body.append('file', optimized);
    body.append('purpose', 'checkpoint_media');
    body.append('visibility', 'deal');
    return uploadFormData<FileAssetDto>('/api/media/', body, onProgress);
  },
  deleteMedia: (id: number) => api<void>(`/api/media/${id}/`, { method: 'DELETE' }),
  uploadDisputeEvidence: async (file: File) => {
    const optimized = await optimizeImageForUpload(file);
    const body = new FormData();
    body.append('file', optimized);
    body.append('purpose', 'dispute_evidence');
    body.append('visibility', 'deal');
    return api<FileAssetDto>('/api/media/', { method: 'POST', body });
  },
  uploadProfileImage: async (file: File, purpose: 'avatar' | 'portfolio' | 'vehicle_photo') => {
    const optimized = await optimizeImageForUpload(file);
    const body = new FormData();
    body.append('file', optimized);
    body.append('purpose', purpose);
    body.append('visibility', 'public');
    return api<FileAssetDto>('/api/media/', { method: 'POST', body });
  },
  updateFileCaption: (id: number, caption: string) =>
    api<FileAssetDto>(`/api/media/${id}/`, { method: 'PATCH', body: { caption } }),
};
